import type { Product } from '../models/ProductModels';

interface JQueryResult {
  mCustomScrollbar(options?: unknown): JQueryResult;
  trigger(eventName: string): JQueryResult;
}

interface BootstrapModal {
  show(): void;
  hide(): void;
}

interface Bootstrap {
  Modal: {
    getOrCreateInstance(element: Element): BootstrapModal;
  };
}

interface AnalyticsProduct {
  name: string;
  id: string;
  price: number | string;
  quantity: string;
  originalPrice: number | string;
  discountPrice: number | string;
  stockStatus: string;
}

type ProductDetails = Array<Record<string, string>>;

function normalizeProductDetails(result: unknown): ProductDetails {
  const response = typeof result === 'string'
    ? (() => {
      try {
        return JSON.parse(result) as unknown;
      } catch {
        return undefined;
      }
    })()
    : result;
  const details = Array.isArray(response)
    ? response
    : response && typeof response === 'object' && 'd' in response && Array.isArray(response.d)
      ? response.d
      : [];

  return details.filter((detail): detail is Record<string, string> => detail !== null && typeof detail === 'object' && !Array.isArray(detail));
}

declare global {
  interface Window {
    $?: (selector: string | Element) => JQueryResult;
    jQuery?: (selector: string | Element) => JQueryResult;
    bootstrap?: Bootstrap;
    dataLayer?: unknown[];
    OpenSubscription?: (element: HTMLElement) => void;
    GetPageProductDetails?: (isbns: string, success: (result: unknown) => void, failure: (error: unknown) => void) => void;
    AssignDataLayerToGTM?: (products: Array<Record<string, unknown>>) => void;
    GAPushTrackEvent?: (category: string, action: string, label: string) => void;
    DLayerAddCart?: (isbn: string, source: string, quantity: number) => void;
    CurrencyFormatted?: (value: number | string) => string;
    grecaptcha?: { reset(): void; ready?(callback: () => void): void; execute?(siteKey: string, options: { action: string }): Promise<string> };
    CengageSearchApp?: { search(query: string): void };
  }
}

function modalFor(selector: string): BootstrapModal | undefined {
  const element = document.querySelector(selector);
  return element ? window.bootstrap?.Modal.getOrCreateInstance(element) : undefined;
}

export function showModal(selector: string): void { modalFor(selector)?.show(); }
export function hideModal(selector: string): void { modalFor(selector)?.hide(); }
export function openSubscription(element: HTMLElement): void { window.OpenSubscription?.(element); }
export function resetRecaptcha(): void { window.grecaptcha?.reset(); }
export function openLogin(): void { document.getElementById('loginlnk')?.click(); }
export function openSignup(url = '/signup.aspx'): void { window.location.assign(url); }

export function getRecaptchaToken(siteKey: string, action: string): Promise<string> {
  const recaptcha = window.grecaptcha;
  if (!siteKey || !recaptcha?.execute) return Promise.resolve('');
  return new Promise((resolve, reject) => {
    const execute = () => recaptcha.execute?.(siteKey, { action }).then(resolve, reject);
    if (recaptcha.ready) recaptcha.ready(execute);
    else execute();
  });
}

export function updateCartCount(value: string): void {
  const cart = document.getElementById('Cart');
  if (cart) cart.textContent = value;
}

export function updateWishListCount(count: number): void {
  const list = document.getElementById('WishList');
  if (list) list.textContent = `(${count})`;
}

export function trackRoute(path: string): void {
  const dataLayer = window.dataLayer = window.dataLayer ?? [];
  dataLayer.push({ event: 'ngRouteChange', attributes: { route: `${window.location.origin}${path}` } });
}

export function trackProducts(products: Product[]): void {
  const dataLayer = window.dataLayer = window.dataLayer ?? [];
  dataLayer.push({ Products: products });
  const source: AnalyticsProduct[] = products.map((product) => ({
    name: product.Title,
    id: product.ISBN13,
    price: product.Price,
    quantity: 'n/a',
    originalPrice: product.Price,
    discountPrice: product.DiscountPrice,
    stockStatus: product.StockStatus
  }));
  if (!window.GetPageProductDetails || !window.AssignDataLayerToGTM) return;
  window.GetPageProductDetails(source.map((product) => product.id).join(', '), (details) => {
    const mapped = normalizeProductDetails(details).map((detail, index) => ({ ...source[index], brand: detail.BRAND, category: detail.CATEGORY }));
    window.AssignDataLayerToGTM?.(mapped);
  }, (error) => console.warn('Product analytics lookup failed.', error));
}

export function trackAddToCart(product: Product): void {
  window.GAPushTrackEvent?.('Add to cart search page', 'Click', `${window.location.origin}${product.ProductURL}`);
  window.DLayerAddCart?.(product.ISBN13, 'Search Result', 1);
}

export function trackAddToList(product: Product, listName: string): void {
  window.GAPushTrackEvent?.('Add to List', product.ISBN13, listName);
}

export function defaultImageUrl(staticUrl: string): string { return `${staticUrl}HERnoimage.png&width=160`; }

export function renderCmsHeader(content: string, background: string): void {
  const header = document.getElementById('SearchCMSHeaderDiv');
  if (!header) return;
  const target = header.querySelector('div') ?? header;
  target.innerHTML = content;
  header.style.background = background;
  header.style.display = content ? '' : 'none';
}

export function highlightDivision(division: string): void {
  const mappings: Record<string, string> = {
    primary: 'dnn_CENGAGESUBMENU_PrimaryLink',
    secondary: 'dnn_CENGAGESUBMENU_SecondaryLink',
    vet: 'dnn_CENGAGESUBMENU_VocationalLink',
    vetb2b: 'dnn_CENGAGESUBMENU_VocationalLink',
    'tafe-rto': 'dnn_CENGAGESUBMENU_VocationalLink',
    university: 'dnn_CENGAGESUBMENU_HigherEducation',
    universityb2b: 'dnn_CENGAGESUBMENU_HigherEducation'
  };
  Object.values(mappings).forEach((id) => document.getElementById(id)?.classList.remove('current-menu-parent'));
  const selected = mappings[division.toLowerCase()];
  if (selected) document.getElementById(selected)?.classList.add('current-menu-parent');
}
